// AdminTaxRates.js — Documentação editável dos regimes fiscais PT (CRUD)
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  BookOpen,
  Scale,
  Banknote,
  Building2,
  UserCheck,
  Briefcase,
  Edit2,
  Save,
  X,
  Plus,
  Trash2,
  RotateCcw,
  Loader2,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = { UserCheck, Briefcase, Building2, Banknote };
const ICON_OPTIONS = ["UserCheck", "Briefcase", "Building2", "Banknote"];

const AdminTaxRates = () => {
  const { user, getAuthHeaders } = useAuth();
  const isAdmin = user?.role === "admin";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/tax-rates`, { headers: getAuthHeaders() });
      setData(res.data);
    } catch (e) {
      toast.error("Erro ao carregar Regimes Fiscais");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const startEdit = () => {
    setDraft(JSON.parse(JSON.stringify(data)));
    setEditing(true);
  };

  const cancelEdit = () => {
    setDraft(null);
    setEditing(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/tax-rates`, draft, { headers: getAuthHeaders() });
      toast.success("Regimes fiscais atualizados com sucesso");
      setEditing(false);
      setDraft(null);
      await fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao guardar");
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = async () => {
    if (!window.confirm("Repor para os valores predefinidos? Esta ação é irreversível.")) return;
    setSaving(true);
    try {
      await axios.post(`${API}/tax-rates/reset`, {}, { headers: getAuthHeaders() });
      toast.success("Reposto para valores predefinidos");
      setEditing(false);
      setDraft(null);
      await fetchData();
    } catch (e) {
      toast.error("Erro ao repor");
    } finally {
      setSaving(false);
    }
  };

  // Helpers — operam sobre o draft
  const updateSection = (idx, patch) => {
    const next = { ...draft };
    next.sections = [...next.sections];
    next.sections[idx] = { ...next.sections[idx], ...patch };
    setDraft(next);
  };
  const updateRow = (sIdx, rIdx, patch) => {
    const next = { ...draft };
    next.sections = [...next.sections];
    next.sections[sIdx] = { ...next.sections[sIdx] };
    next.sections[sIdx].rows = [...next.sections[sIdx].rows];
    next.sections[sIdx].rows[rIdx] = { ...next.sections[sIdx].rows[rIdx], ...patch };
    setDraft(next);
  };
  const addRow = (sIdx) => {
    const next = { ...draft };
    next.sections = [...next.sections];
    next.sections[sIdx] = { ...next.sections[sIdx] };
    next.sections[sIdx].rows = [
      ...next.sections[sIdx].rows,
      { _key: `row_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`, label: "Nova taxa", value: "", note: "" },
    ];
    setDraft(next);
  };
  const removeRow = (sIdx, rIdx) => {
    const next = { ...draft };
    next.sections = [...next.sections];
    next.sections[sIdx] = { ...next.sections[sIdx] };
    next.sections[sIdx].rows = next.sections[sIdx].rows.filter((_, i) => i !== rIdx);
    setDraft(next);
  };
  const addSection = () => {
    const next = { ...draft };
    next.sections = [
      ...next.sections,
      { id: `section_${Date.now()}`, title: "Nova Secção", icon: "Banknote", rows: [] },
    ];
    setDraft(next);
  };
  const removeSection = (idx) => {
    if (!window.confirm("Remover esta secção?")) return;
    const next = { ...draft };
    next.sections = next.sections.filter((_, i) => i !== idx);
    setDraft(next);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 font-sans">
        <Loader2 className="w-8 h-8 animate-spin text-brand-600" />
      </div>
    );
  }

  const view = editing ? draft : data;
  const updatedDate = view?.updated_at ? new Date(view.updated_at).toLocaleString("pt-PT") : "—";

  return (
    <div className="min-h-screen bg-slate-50 font-sans" data-testid="admin-tax-rates-page">
      <div className="container mx-auto px-4 py-6 max-w-5xl">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/admin">
              <Button variant="outline" size="sm" className="border-slate-300" data-testid="back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </Button>
            </Link>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-brand-600 flex items-center gap-2">
                <BookOpen className="w-6 h-6" />
                Regimes Fiscais {view?.year_label || "PT 2026"}
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                {view?.subtitle ||
                  "Documentação das taxas e fórmulas atualmente aplicadas pelo simulador."}
              </p>
              <p className="text-[11px] text-slate-400 mt-1">
                Última atualização: <span className="font-mono">{updatedDate}</span>
                {view?.updated_by && <> · por <span className="font-mono">{view.updated_by}</span></>}
              </p>
            </div>
          </div>

          {/* Action buttons */}
          {isAdmin && (
            <div className="flex items-center gap-2">
              {!editing && (
                <>
                  <Button
                    onClick={startEdit}
                    size="sm"
                    className="bg-brand-600 hover:bg-brand-700 text-white"
                    data-testid="edit-btn"
                  >
                    <Edit2 className="w-4 h-4 mr-2" />
                    Editar
                  </Button>
                  <Button
                    onClick={resetDefaults}
                    size="sm"
                    variant="outline"
                    className="border-amber-300 text-amber-700 hover:bg-amber-50"
                    disabled={saving}
                    data-testid="reset-btn"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Repor defaults
                  </Button>
                </>
              )}
              {editing && (
                <>
                  <Button
                    onClick={save}
                    size="sm"
                    className="bg-brand-600 hover:bg-brand-700 text-white"
                    disabled={saving}
                    data-testid="save-btn"
                  >
                    {saving ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Guardar
                  </Button>
                  <Button
                    onClick={cancelEdit}
                    size="sm"
                    variant="outline"
                    className="border-slate-300"
                    disabled={saving}
                    data-testid="cancel-btn"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Cancelar
                  </Button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Editable global meta */}
        {editing && (
          <Card className="mb-4 bg-white border-brand-200">
            <CardContent className="p-3 grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-slate-500">Ano fiscal (etiqueta)</label>
                <Input
                  value={draft.year_label || ""}
                  onChange={(e) => setDraft({ ...draft, year_label: e.target.value })}
                  className="h-8 text-sm mt-1"
                  data-testid="year-label-input"
                />
              </div>
              <div>
                <label className="text-xs text-slate-500">Subtítulo</label>
                <Input
                  value={draft.subtitle || ""}
                  onChange={(e) => setDraft({ ...draft, subtitle: e.target.value })}
                  className="h-8 text-sm mt-1"
                  data-testid="subtitle-input"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Sections grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {(view?.sections || []).map((section, sIdx) => {
            const Icon = ICONS[section.icon] || Banknote;
            return (
              <Card key={section.id || sIdx} className="bg-white border-slate-200" data-testid={`section-${section.id || sIdx}`}>
                <CardHeader className="pb-3">
                  {editing ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4 text-brand-600 flex-shrink-0" />
                        <Input
                          value={section.title}
                          onChange={(e) => updateSection(sIdx, { title: e.target.value })}
                          className="h-8 text-sm font-bold text-brand-600"
                          data-testid={`section-title-input-${sIdx}`}
                        />
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => removeSection(sIdx)}
                          className="h-7 w-7 text-red-500 hover:text-red-700 hover:bg-red-50 flex-shrink-0"
                          data-testid={`remove-section-${sIdx}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="text-[11px] text-slate-500">Ícone:</label>
                        <select
                          value={section.icon}
                          onChange={(e) => updateSection(sIdx, { icon: e.target.value })}
                          className="text-xs border border-slate-300 rounded h-7 px-2"
                          data-testid={`section-icon-${sIdx}`}
                        >
                          {ICON_OPTIONS.map((ic) => (
                            <option key={ic} value={ic}>{ic}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  ) : (
                    <CardTitle className="text-base font-bold text-brand-600 flex items-center gap-2">
                      <Icon className="w-4 h-4" />
                      {section.title}
                    </CardTitle>
                  )}
                </CardHeader>
                <CardContent className="text-sm text-slate-700 space-y-2">
                  {(section.rows || []).map((row, rIdx) =>
                    editing ? (
                      <div
                        key={row._key || `${sIdx}-${row.label}-${rIdx}`}
                        className="grid grid-cols-12 gap-2 py-1.5 border-b border-slate-100 last:border-0 items-start"
                      >
                        <div className="col-span-5">
                          <Input
                            value={row.label}
                            onChange={(e) => updateRow(sIdx, rIdx, { label: e.target.value })}
                            className="h-7 text-xs"
                            placeholder="Designação"
                            data-testid={`row-label-${sIdx}-${rIdx}`}
                          />
                          <Textarea
                            value={row.note || ""}
                            onChange={(e) => updateRow(sIdx, rIdx, { note: e.target.value })}
                            className="text-[10px] mt-1 min-h-[40px] resize-none"
                            placeholder="Nota / referência legal"
                            data-testid={`row-note-${sIdx}-${rIdx}`}
                          />
                        </div>
                        <div className="col-span-6">
                          <Input
                            value={row.value}
                            onChange={(e) => updateRow(sIdx, rIdx, { value: e.target.value })}
                            className="h-7 text-xs font-mono"
                            placeholder="Valor"
                            data-testid={`row-value-${sIdx}-${rIdx}`}
                          />
                        </div>
                        <div className="col-span-1 flex justify-end">
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => removeRow(sIdx, rIdx)}
                            className="h-7 w-7 text-red-500 hover:text-red-700 hover:bg-red-50"
                            data-testid={`remove-row-${sIdx}-${rIdx}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div
                        key={row._key || `${sIdx}-${row.label}-${rIdx}`}
                        className="flex items-start justify-between gap-3 py-1.5 border-b border-slate-100 last:border-0"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-slate-800">{row.label}</div>
                          {row.note && (
                            <div className="text-[11px] text-slate-500 mt-0.5">{row.note}</div>
                          )}
                        </div>
                        <Badge
                          variant="outline"
                          className="font-mono border-brand-600 text-brand-700 bg-brand-50 whitespace-nowrap"
                        >
                          {row.value}
                        </Badge>
                      </div>
                    )
                  )}
                  {editing && (
                    <Button
                      onClick={() => addRow(sIdx)}
                      size="sm"
                      variant="outline"
                      className="w-full mt-1 border-dashed border-brand-300 text-brand-600 hover:bg-brand-50"
                      data-testid={`add-row-${sIdx}`}
                    >
                      <Plus className="w-3.5 h-3.5 mr-1" />
                      Adicionar linha
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}

          {editing && (
            <Card className="bg-white border-dashed border-brand-300 flex items-center justify-center min-h-[120px]">
              <Button
                onClick={addSection}
                variant="ghost"
                className="text-brand-600 hover:bg-brand-50"
                data-testid="add-section-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Adicionar Secção
              </Button>
            </Card>
          )}
        </div>

        {/* Nota legal */}
        <Card className="mt-4 bg-amber-50 border-amber-200">
          <CardContent className="p-4">
            <div className="flex gap-3">
              <Scale className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-amber-800 space-y-1">
                <p className="font-semibold">Nota Legal</p>
                <p>
                  Esta página resume os parâmetros configurados no simulador para o ano fiscal
                  indicado. Para alterar valores que afetam <strong>cálculos</strong> (e.g. limites
                  SS, taxas IRS, plafonds de benefícios), use os ecrãs dedicados em{" "}
                  <code className="bg-amber-100 px-1 rounded">/admin/irs</code>,{" "}
                  <code className="bg-amber-100 px-1 rounded">/admin/ss</code>,{" "}
                  <code className="bg-amber-100 px-1 rounded">/admin/benefits</code>. Esta página é
                  apenas <strong>documentação editável</strong> — as alterações aqui não impactam
                  cálculos automáticos.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminTaxRates;
